import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer, type ReactNodeViewProps } from '@tiptap/react';
import { ResizableWrap } from './ResizableWrap';

// Generic iframe embed node. A single node type handles every provider; the
// `src` is computed from the canonical `url` attr via a provider registry so
// storing the user's original URL round-trips cleanly and we can update the
// embed math later without a migration.
//
// Known providers (YouTube, Vimeo, Figma, Loom, Spotify, CodePen, CodeSandbox,
// Twitch, Miro, GitHub Gist) get a proper embed URL + aspect ratio. Unknown
// URLs render a compact link card — iframing random sites usually gets blocked
// by X-Frame-Options and looks broken, so we don't try.

type EmbedAttrs = {
  url: string;
  provider: string;
  // Pixel width. null = full column. The aspect-ratio frame inside adjusts
  // height automatically.
  width: number | null;
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    embed: {
      insertEmbed: (url: string) => ReturnType;
    };
  }
}

export const Embed = Node.create({
  name: 'embed',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      url: { default: '' },
      provider: { default: 'link' },
      width: { default: null },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-embed-url]',
        getAttrs: (el) => {
          const node = el as HTMLElement;
          const widthStr = node.getAttribute('data-embed-width');
          const width = widthStr ? Number(widthStr) : null;
          return {
            url: node.getAttribute('data-embed-url') ?? '',
            provider: node.getAttribute('data-embed-provider') ?? 'link',
            width: Number.isFinite(width) ? width : null,
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-embed-url': HTMLAttributes.url,
        'data-embed-provider': HTMLAttributes.provider,
        'data-embed-width': HTMLAttributes.width ?? '',
      }),
    ];
  },

  addCommands() {
    return {
      insertEmbed:
        (input: string) =>
        ({ commands }) => {
          // Accept both a bare URL and a full `<iframe>` snippet; extract the
          // src in the latter case so the user can paste whatever Figma/Loom
          // gave them without cleaning it up first.
          const url = extractIframeSrc(input) ?? input.trim();
          const { provider } = resolveEmbed(url);
          return commands.insertContent({ type: 'embed', attrs: { url, provider } });
        },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(EmbedView);
  },
});

function EmbedView({ node, updateAttributes, editor }: ReactNodeViewProps<HTMLElement>) {
  const attrs = node.attrs as EmbedAttrs;
  const resolved = resolveEmbed(attrs.url);
  const editable = editor?.isEditable ?? false;

  return (
    <NodeViewWrapper>
      <ResizableWrap
        width={attrs.width}
        onResize={(w) => updateAttributes({ width: w })}
        editable={editable}
        className="embed-block"
      >
        <div data-provider={resolved.provider} contentEditable={false}>
          {resolved.embedUrl ? (
            <div
              className="embed-frame"
              style={{ paddingBottom: `${(1 / resolved.aspect) * 100}%` }}
            >
              <iframe
                src={resolved.embedUrl}
                title={resolved.title ?? attrs.url}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          ) : (
            <LinkCard url={attrs.url} />
          )}
          <div className="embed-caption">
            <span className="embed-provider">{resolved.provider}</span>
            <a href={attrs.url} target="_blank" rel="noreferrer" className="embed-open">
              {truncate(attrs.url, 72)}
            </a>
          </div>
        </div>
      </ResizableWrap>
    </NodeViewWrapper>
  );
}

function LinkCard({ url }: { url: string }) {
  let hostname = '';
  try {
    hostname = new URL(url).hostname.replace(/^www\./, '');
  } catch {
    hostname = url;
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className="embed-link-card">
      <div className="embed-link-host">{hostname}</div>
      <div className="embed-link-url">{truncate(url, 120)}</div>
      <div className="embed-link-open">Open ↗</div>
    </a>
  );
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : s.slice(0, n - 1) + '…';
}

// Provider registry. Each matcher maps a canonical URL → embed URL + aspect.
// Order matters only for overlapping host rules; keep the specific matcher
// (e.g. youtube playlist) before the general one if we add it.
type Resolved = {
  provider: string;
  embedUrl: string | null;
  aspect: number;
  title?: string;
};

// Exported so the editor's paste handler can reuse the same provider detection
// without constructing a temporary editor command.
export function resolveEmbed(raw: string): Resolved {
  const url = safeUrl(raw);
  if (!url) return { provider: 'link', embedUrl: null, aspect: 16 / 9 };
  const host = url.hostname.replace(/^www\./, '');

  // YouTube: youtu.be/<id> or youtube.com/watch?v=<id> or /shorts/<id> or /live/<id>
  if (host === 'youtu.be') {
    const id = url.pathname.slice(1).split('/')[0];
    if (id) return yt(id, url.searchParams.get('t'));
  }
  if (host === 'youtube.com' || host.endsWith('.youtube.com')) {
    const v = url.searchParams.get('v');
    const shortsMatch = url.pathname.match(/^\/(shorts|live|embed)\/([A-Za-z0-9_-]+)/);
    const id = v ?? shortsMatch?.[2];
    if (id) return yt(id, url.searchParams.get('t'));
  }

  // Vimeo: vimeo.com/<id>
  if (host === 'vimeo.com') {
    const id = url.pathname.match(/^\/(\d+)/)?.[1];
    if (id)
      return {
        provider: 'vimeo',
        embedUrl: `https://player.vimeo.com/video/${id}`,
        aspect: 16 / 9,
      };
  }

  // Figma. Four input shapes land here:
  //   (1) canonical file URL — figma.com/(file|design|proto|board|slides)/:id/…
  //   (2) already-formed embed URL — figma.com/embed?url=…
  //   (3) Figma's new embed subdomain — embed.figma.com/(design|proto|…)/:id
  //       (this is the URL inside Figma's current "Copy link to embed" snippet)
  //   (4) community share — figma.com/community/file/:id/…
  // For (2) and (3) the URL is already embed-ready, so don't double-wrap.
  // `embed_host=share` is the value Figma itself uses; safest across accounts.
  if (host === 'embed.figma.com') {
    return { provider: 'figma', embedUrl: raw, aspect: 16 / 10 };
  }
  if (host === 'figma.com' || host.endsWith('.figma.com')) {
    if (url.pathname === '/embed') {
      return { provider: 'figma', embedUrl: raw, aspect: 16 / 10 };
    }
    return {
      provider: 'figma',
      embedUrl: `https://www.figma.com/embed?embed_host=share&url=${encodeURIComponent(raw)}`,
      aspect: 16 / 10,
    };
  }

  // Loom: loom.com/share/<id>
  if (host === 'loom.com') {
    const id = url.pathname.match(/\/share\/([A-Za-z0-9]+)/)?.[1];
    if (id)
      return {
        provider: 'loom',
        embedUrl: `https://www.loom.com/embed/${id}`,
        aspect: 16 / 9,
      };
  }

  // Spotify: open.spotify.com/<type>/<id>
  if (host === 'open.spotify.com') {
    const m = url.pathname.match(/^\/(track|episode|playlist|album|show)\/([A-Za-z0-9]+)/);
    if (m)
      return {
        provider: 'spotify',
        embedUrl: `https://open.spotify.com/embed/${m[1]}/${m[2]}`,
        aspect: m[1] === 'track' || m[1] === 'episode' ? 100 / 30 : 16 / 10,
      };
  }

  // CodePen: codepen.io/<user>/pen/<id>
  if (host === 'codepen.io') {
    const m = url.pathname.match(/^\/([^/]+)\/pen\/([A-Za-z0-9]+)/);
    if (m)
      return {
        provider: 'codepen',
        embedUrl: `https://codepen.io/${m[1]}/embed/${m[2]}?default-tab=result`,
        aspect: 16 / 10,
      };
  }

  // CodeSandbox: codesandbox.io/s/<id>
  if (host === 'codesandbox.io') {
    const id = url.pathname.match(/^\/s\/([A-Za-z0-9-]+)/)?.[1];
    if (id)
      return {
        provider: 'codesandbox',
        embedUrl: `https://codesandbox.io/embed/${id}`,
        aspect: 16 / 10,
      };
  }

  // Twitch: twitch.tv/<channel> or twitch.tv/videos/<id>
  if (host === 'twitch.tv') {
    const vid = url.pathname.match(/^\/videos\/(\d+)/)?.[1];
    const channel = url.pathname.match(/^\/([A-Za-z0-9_]+)/)?.[1];
    const parent = window.location.hostname;
    if (vid) {
      return {
        provider: 'twitch',
        embedUrl: `https://player.twitch.tv/?video=${vid}&parent=${parent}`,
        aspect: 16 / 9,
      };
    }
    if (channel) {
      return {
        provider: 'twitch',
        embedUrl: `https://player.twitch.tv/?channel=${channel}&parent=${parent}`,
        aspect: 16 / 9,
      };
    }
  }

  // Miro: miro.com/app/board/<id>
  if (host === 'miro.com') {
    const m = url.pathname.match(/\/app\/board\/([^/]+)/);
    if (m)
      return {
        provider: 'miro',
        embedUrl: `https://miro.com/app/live-embed/${m[1]}`,
        aspect: 16 / 10,
      };
  }

  // GitHub gist
  if (host === 'gist.github.com') {
    return {
      provider: 'gist',
      embedUrl: `${raw}.pibb`,
      aspect: 16 / 10,
    };
  }

  return { provider: hostOrLink(host), embedUrl: null, aspect: 16 / 9 };
}

function yt(id: string, t: string | null): Resolved {
  const startSec = parseStart(t);
  const qs = new URLSearchParams({ rel: '0' });
  if (startSec) qs.set('start', String(startSec));
  return {
    provider: 'youtube',
    embedUrl: `https://www.youtube.com/embed/${id}?${qs.toString()}`,
    aspect: 16 / 9,
  };
}

function parseStart(t: string | null): number | null {
  if (!t) return null;
  if (/^\d+$/.test(t)) return Number(t);
  const match = t.match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?/);
  if (!match) return null;
  const [, h = '0', m = '0', s = '0'] = match;
  return Number(h) * 3600 + Number(m) * 60 + Number(s);
}

function hostOrLink(host: string): string {
  return host || 'link';
}

function safeUrl(raw: string): URL | null {
  try {
    return new URL(raw);
  } catch {
    return null;
  }
}

// Detect whether a bare pasted string is a URL we'd embed. Used by the editor's
// paste handler to auto-convert single-line URL pastes into embed nodes.
export function isEmbeddableUrl(text: string): string | null {
  const trimmed = text.trim();
  if (!/^https?:\/\//i.test(trimmed)) return null;
  if (/\s/.test(trimmed)) return null;
  const url = safeUrl(trimmed);
  if (!url) return null;
  const { provider } = resolveEmbed(trimmed);
  return provider === 'link' ? null : trimmed;
}

// Extract a usable embed URL from a pasted `<iframe>` tag or raw HTML fragment.
// Figma, Loom, YouTube, Spotify, and friends all expose a "Copy embed code"
// option that yields an iframe snippet; paste that and we'll harvest the src.
export function extractIframeSrc(html: string): string | null {
  const match = html.match(/<iframe\b[^>]*\bsrc\s*=\s*["']([^"']+)["']/i);
  if (!match) return null;
  const src = match[1]!;
  // Decode any HTML entities the clipboard may have introduced (&amp; etc.).
  const decoded = src
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#x2F;/g, '/')
    .replace(/&#39;/g, "'");
  return /^https?:\/\//i.test(decoded) ? decoded : null;
}
